/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-prototype-builtins */
import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useLayoutEffect,
} from 'react'
import { SzInput } from 'react-theme-components'
import { Modal } from 'react-bootstrap'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { AppContext } from 'store/context'
import { isMobile } from 'utils/Common/screen-utils'
import { truncateString } from 'utils/Common/ts-utils'
import EnumFilterType from 'utils/__enums/Filters/FilterBuilder'
import {
  FilterDynamicItem,
  FilterStaticItem,
  StatusOption,
  ArrayType,
  filterBuilder,
  StringType,
} from 'utils/__interfaces/Filters/FilterBuilderInterface'
import defaultFilter from 'utils/__consts/Filters/FilterBuilderInterface'
import { checkRangeLimit } from 'utils/Filters/filter-utils'
import CustomSelect from 'components/Selectors/CustomSelect'
import Button from 'components/Buttons/Button'
import SvgIcon from 'components/Icons/SvgIcon'
import ButtonSquareIcon from 'components/Buttons/ButtonSquareIcon'
import DatePicker from 'components/DatePickers/DatePicker'
import FormDropdownInput from 'components/Forms/FormDropdownInput'
import MonthRangePicker from 'components/DatePickers/MonthRangePicker'
import Tags from './Tags'

interface Props {
  onSubmitFilter: (values: { [key: string]: any | any[] }) => void
  onResetFilter: () => void
  filterBuilder: filterBuilder
  className?: string
  initialFilterValues?: { [key: string]: any | any[] }
}

const FilterBuilder: React.FC<Props> = ({
  onSubmitFilter,
  onResetFilter,
  filterBuilder,
  className,
  initialFilterValues,
}: Props) => {
  const { t } = useTranslation()
  const {
    state: { institution, organization },
  } = useContext(AppContext)

  type FilterItem = FilterStaticItem | FilterDynamicItem

  const [
    isMonthPickerPartiallyFilled,
    setIsMonthPickerPartiallyFilled,
  ] = useState<boolean>(false)

  const initialeDate = new Date()
  const [isMobileMode, setIsMobileMode] = useState<boolean>(
    isMobile(window.innerWidth)
  )

  const filterOptions = filterBuilder.dynamic.map((item) => ({
    label: item.label,
    value: item.name,
    type: item.type,
    multiple: item.multiple,
  }))

  const [selectedFilter, setSelectedFilter] = useState<StatusOption>(
    defaultFilter
  )
  const [resetDate, setResetDate] = useState<boolean>(false)
  const [showLimitDatesError, setShowLimitDatesError] = useState<boolean>(false)

  const initializFiltersArray = () => {
    return filterBuilder.dynamic.reduce((acc, curr) => {
      acc[curr.name] = []
      return acc
    }, {} as ArrayType)
  }

  const initializFiltersString = <T extends FilterItem>(filterItems: T[]) => {
    return filterItems.reduce((acc, curr) => {
      switch (curr.type) {
        case EnumFilterType.INPUT_DATEPICKER:
          acc[curr.name] = {
            startDate: curr?.initialStartDate ?? null,
            endDate: curr?.initialEndDate ?? null,
          }
          break
        case EnumFilterType.INPUT_MONTH_DATEPICKER:
          acc[curr.name] = {
            startDateMonth: curr?.initialStartDate ?? null,
            endDateMonth: curr?.initialEndDate ?? null,
          }
          break
        case EnumFilterType.INPUT_MONTH_PICKER:
          acc[curr.name] = {
            startDateMonth: curr?.initialStartDate ?? null,
            endDateMonth: curr?.initialEndDate ?? null,
          }
          break
        case EnumFilterType.INPUT_DROPDOWN:
          acc[curr.name] = curr.data
            ? curr.data[0]
            : { label: t('filter.chooseOption'), value: 'all' }
          break
        case EnumFilterType.INPUT_NUMBER:
          acc[curr.name] = 0
          break
        case EnumFilterType.INPUT_WITH_OPERATOR:
          acc[curr.name] = {
            operator: curr.data ? curr.data[0].value : '',
            label: curr.data ? curr.data[0].label : '',
            value: '',
          }
          break
        default:
          acc[curr.name] = ''
          break
      }
      return acc
    }, {} as StringType)
  }

  const getInitialDynamicFilterValues = () => {
    const baseValues = initializFiltersArray()

    if (initialFilterValues) {
      filterBuilder.dynamic.forEach((item) => {
        if (initialFilterValues[item.apiName]) {
          if (item.multiple) {
            baseValues[item.name] = Array.isArray(
              initialFilterValues[item.apiName]
            )
              ? initialFilterValues[item.apiName]
              : [initialFilterValues[item.apiName]]
          } else {
            baseValues[item.name] = [initialFilterValues[item.apiName]]
          }
        }
      })
    }

    return baseValues
  }

  const [filterValues, setFiltersValues] = useState<ArrayType>(
    getInitialDynamicFilterValues()
  )

  const [tmpDynamicFilterState, setTmpDynamicFilterState] = useState<
    StringType
  >(initializFiltersString(filterBuilder.dynamic))

  const [tmpStaticFilterState, setTmpStaticFilterState] = useState<StringType>(
    initializFiltersString(filterBuilder.static)
  )

  const isDynamicFilterStateChanged =
    JSON.stringify(tmpDynamicFilterState) !==
    JSON.stringify(initializFiltersString(filterBuilder.dynamic))

  const isStaticFilterStateChanged =
    JSON.stringify(tmpStaticFilterState) !==
    JSON.stringify(initializFiltersString(filterBuilder.static))

  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true)
  const [showModal, setShowModal] = useState(false)
  const [showTags, setShowTags] = useState<boolean>(false)
  const [fixedHeight, setFixedHeight] = useState<boolean>(false)
  const tagBlockHeight = useRef(0)
  const nbTagRows = useRef(0)

  const checkDeviceType = () => {
    setIsMobileMode(isMobile(window.innerWidth))
  }

  useLayoutEffect(() => {
    const tagsHeight = document.getElementById('filter-tags')?.clientHeight
    if (tagsHeight === undefined) {
      nbTagRows.current = 0
    }
    if (tagsHeight !== undefined) {
      if (tagsHeight > tagBlockHeight.current) {
        nbTagRows.current += 1
      }
      if (tagsHeight < tagBlockHeight.current) {
        nbTagRows.current -= 1
      }
      tagBlockHeight.current = tagsHeight
    }
  })

  useEffect(() => {
    window.addEventListener('resize', checkDeviceType)
    // Don't remove the return, otherwise you'll have memory leak
    return () => window.removeEventListener('resize', checkDeviceType)
  }, [window.innerWidth])

  useEffect(() => {
    const filterDesktopHeight = document.getElementById('filter-module-desktop')
      ?.clientHeight
    if (nbTagRows.current === 5) {
      if (filterDesktopHeight !== undefined) {
        document.documentElement.style.setProperty(
          '--max-filter-height',
          `${filterDesktopHeight}px`
        )
      }
      setFixedHeight(true)
    } else if (nbTagRows.current < 5) {
      setFixedHeight(false)
    }
  })

  useEffect(() => {
    setShowTags(
      Object.values(filterValues).some((filter: any) => filter.length > 0)
    )
  })

  useEffect(() => {
    if (isStaticFilterStateChanged) setIsSubmitDisabled(false)
  }, [tmpStaticFilterState])

  useEffect(() => {
    if (
      JSON.stringify(filterValues) !== JSON.stringify(initializFiltersArray())
    )
      setIsSubmitDisabled(false)
  }, [filterValues])

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (isDynamicFilterStateChanged) {
          addTag()
        }
        if (isStaticFilterStateChanged) {
          applyFilter()
        }
      }
    }
    window.addEventListener('keypress', handleKeyPress)
    return () => window.removeEventListener('keypress', handleKeyPress)
  }, [tmpDynamicFilterState, tmpStaticFilterState])

  useEffect(() => {
    resetFilter()
  }, [institution, organization])

  useEffect(() => {
    if (
      initialFilterValues &&
      Object.keys(initialFilterValues).some(
        (key) => key !== 'anneeMois' && initialFilterValues[key]?.length > 0
      )
    ) {
      const updatedFilterValues = { ...filterValues }

      filterBuilder.dynamic.forEach((item) => {
        if (initialFilterValues[item.apiName]) {
          if (item.multiple) {
            updatedFilterValues[item.name] = Array.isArray(
              initialFilterValues[item.apiName]
            )
              ? initialFilterValues[item.apiName]
              : [initialFilterValues[item.apiName]]
          } else {
            updatedFilterValues[item.name] = [initialFilterValues[item.apiName]]
          }
        }
      })

      setFiltersValues(updatedFilterValues)

      applyFilter()
    }
  }, [])

  useEffect(() => {
    const monthPickerFilter = filterBuilder.static.find(
      (item) => item.type === EnumFilterType.INPUT_MONTH_PICKER
    )

    if (monthPickerFilter) {
      const startDate =
        tmpStaticFilterState[monthPickerFilter.name]?.startDateMonth
      const endDate = tmpStaticFilterState[monthPickerFilter.name]?.endDateMonth

      const isPartiallyFilled =
        (startDate !== null && endDate === null) ||
        (startDate === null && endDate !== null)
      setIsMonthPickerPartiallyFilled(isPartiallyFilled)
    }
  }, [])

  const onHideUpdate = async () => {
    setShowModal(!showModal)
  }

  const resetFilter = () => {
    setTmpDynamicFilterState(initializFiltersString(filterBuilder.dynamic))
    setTmpStaticFilterState(initializFiltersString(filterBuilder.static))
    setSelectedFilter(defaultFilter)
    setFiltersValues(initializFiltersArray())
    setIsSubmitDisabled(true)
    setShowLimitDatesError(false)
    setIsMonthPickerPartiallyFilled(false)
    setResetDate(true)
    onResetFilter()
  }

  const applyFilter = () => {
    // Remplacer les clés des filtres (name) par l'apiName pour éviter de les convertir dans le service
    const results: { [key: string]: any | any[] } = {}
    filterBuilder.dynamic.forEach((item: FilterDynamicItem) => {
      if (item.multiple) {
        const trimmedValues = filterValues[item.name].map((val: any) =>
          typeof val === 'string' ? val.trim() : val
        )
        results[item.apiName] = results.hasOwnProperty(item.apiName)
          ? [...results[item.apiName], ...trimmedValues]
          : trimmedValues
      } else {
        const [firstValue] = filterValues[item.name]
        results[item.apiName] =
          typeof firstValue === 'string' ? firstValue.trim() : firstValue
      }
    })

    filterBuilder.static.forEach((item: FilterStaticItem) => {
      const value = tmpStaticFilterState[item.name]
      results[item.apiName] = typeof value === 'string' ? value.trim() : value
    })

    if (
      !isStaticFilterStateChanged &&
      JSON.stringify(filterValues) === JSON.stringify(initializFiltersArray())
    )
      setIsSubmitDisabled(true)

    onSubmitFilter(results)
    setShowModal(false)
  }

  const onChangeFilter = (e: StatusOption) => {
    setSelectedFilter(e)
    setTmpDynamicFilterState(initializFiltersString(filterBuilder.dynamic))
  }

  /**
   * Gère le collage de texte dans les champs de filtres en nettoyant les espaces
   * et en maintenant la position du curseur
   */
  const handlePasteWithTrim = (
    e: React.ClipboardEvent<HTMLInputElement>,
    fieldValue: string,
    tmpFilterState: StringType,
    setTmpFilterState: React.Dispatch<React.SetStateAction<StringType>>
  ) => {
    const pastedText = e.clipboardData.getData('text').trim()

    // Empêcher le collage si le texte est vide après nettoyage
    if (!pastedText) {
      e.preventDefault()
      return
    }

    e.preventDefault()

    const input = e.currentTarget
    const currentValue = input.value || ''
    const selectionStart = input.selectionStart || 0
    const selectionEnd = input.selectionEnd || 0

    // Construire la nouvelle valeur en remplaçant la sélection par le texte collé
    const newValue =
      currentValue.substring(0, selectionStart) +
      pastedText +
      currentValue.substring(selectionEnd)

    setTmpFilterState({
      ...tmpFilterState,
      [fieldValue]: newValue,
    })

    // Repositionner le curseur après le texte collé
    requestAnimationFrame(() => {
      const newCursorPosition = selectionStart + pastedText.length
      input.setSelectionRange(newCursorPosition, newCursorPosition)
    })
  }

  const renderFilterComponent = <T extends FilterItem>(
    filterItems: T[],
    tmpFilterState: StringType,
    setTmpFilterState: React.Dispatch<React.SetStateAction<StringType>>,
    type: string,
    value: string
  ) => {
    const placeholder = !isMobileMode
      ? truncateString(
          filterItems.find((item) => item.name === value)?.placeholder ?? '',
          25
        )
      : filterItems.find((item) => item.name === value)?.placeholder ?? ''
    const id = `${value}-input`
    const intiValue = tmpFilterState[value]

    switch (type) {
      case EnumFilterType.INPUT_DATEPICKER: {
        const isInitializedDates =
          tmpFilterState[value].startDate !== null ||
          tmpFilterState[value].endDate != null

        const outsideRange =
          filterItems.find((item) => item.name === value)?.outsideRange ?? false

        const rangeLimit =
          filterItems.find((item) => item.name === value)?.rangeLimit ?? false

        const subtractEndDay =
          filterItems.find((item) => item.name === value)?.subtractEndDay ??
          false

        return (
          <>
            <DatePicker
              initialstartDate={
                tmpFilterState[value].startDate
                  ? new Date(tmpFilterState[value].startDate)
                  : initialeDate
              }
              initialendDate={
                tmpFilterState[value].endDate
                  ? new Date(tmpFilterState[value].endDate)
                  : initialeDate
              }
              initialDateDisplayed={isInitializedDates}
              handleDatesChange={([startDate, endDate]: [
                Date | null,
                Date | null
              ]) => {
                setResetDate(false)
                let processedEndDate = endDate
                if (subtractEndDay && endDate) {
                  processedEndDate = moment(endDate).subtract(1, 'day').toDate()
                }
                setTmpFilterState({
                  ...tmpFilterState,
                  [value]: {
                    startDate: moment(startDate).format('DD/MM/YYYY'),
                    endDate: moment(processedEndDate).format('DD/MM/YYYY'),
                  },
                })
                if (
                  rangeLimit &&
                  checkRangeLimit(startDate, endDate, rangeLimit)
                )
                  setShowLimitDatesError(true)
                else setShowLimitDatesError(false)
              }}
              resetDate={resetDate}
              debounceDuration={0}
              isOutsideRange={(day: moment.Moment) => {
                if (outsideRange) return false
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                day.startOf('day')
                return day.isAfter(today)
              }}
            />
            {showLimitDatesError && (
              <span className="datePicker-error">
                {t('filter.errors.datePickerRangeLimit')}
              </span>
            )}
          </>
        )
      }
      case EnumFilterType.INPUT_MONTH_DATEPICKER: {
        const isInitializedDates =
          tmpFilterState[value].startDateMonth !== null ||
          tmpFilterState[value].endDateMonth != null
        const rangeLimit =
          filterItems.find((item) => item.name === value)?.rangeLimit ?? false

        return (
          <>
            <DatePicker
              initialstartDate={
                tmpFilterState[value].startDateMonth ?? initialeDate
              }
              initialendDate={
                tmpFilterState[value].endDateMonth ?? initialeDate
              }
              initialDateDisplayed={isInitializedDates}
              handleDatesChange={([startDate, endDate]: [
                Date | null,
                Date | null
              ]) => {
                setResetDate(false)
                setTmpFilterState({
                  ...tmpFilterState,
                  [value]: {
                    startDateMonth: moment(startDate).format('MM/YYYY'),
                    endDateMonth: moment(endDate).format('MM/YYYY'),
                  },
                })
                if (
                  rangeLimit &&
                  checkRangeLimit(startDate, endDate, rangeLimit)
                )
                  setShowLimitDatesError(true)
                else setShowLimitDatesError(false)
              }}
              resetDate={resetDate}
              debounceDuration={0}
              isOutsideRange={(day: moment.Moment) => {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                day.startOf('day')
                return day.isAfter(today)
              }}
              isMonthly
            />
            {showLimitDatesError && (
              <span className="datePicker-error">
                {t('filter.errors.datePickerRangeLimit')}
              </span>
            )}
          </>
        )
      }
      case EnumFilterType.INPUT_MONTH_PICKER:
        return (
          <MonthRangePicker
            initialstartDate={
              tmpFilterState[value].startDateMonth ?? initialeDate
            }
            initialendDate={tmpFilterState[value].endDateMonth ?? initialeDate}
            handleDatesChange={([startDate, endDate]: [
              Date | null,
              Date | null
            ]) => {
              setResetDate(false)
              const isPartiallyFilled =
                (startDate !== null && endDate === null) ||
                (startDate === null && endDate !== null)
              setIsMonthPickerPartiallyFilled(isPartiallyFilled)
              setTmpFilterState({
                ...tmpFilterState,
                [value]: {
                  startDateMonth: moment(startDate).format('MM/YYYY'),
                  endDateMonth: moment(endDate).format('MM/YYYY'),
                },
              })
            }}
            resetDate={resetDate}
          />
        )
      case EnumFilterType.INPUT_DROPDOWN:
        return (
          <CustomSelect
            id={id}
            options={filterItems.find((item) => item.name === value)?.data}
            value={tmpFilterState[value]}
            className="searchBar_select"
            name="input-1"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setTmpFilterState({
                ...tmpFilterState,
                [value]: e,
              })
            }}
            height={47}
          />
        )
      case EnumFilterType.INPUT_NUMBER:
        return (
          <SzInput
            id={id}
            value={intiValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (/^[-]{0}\d{0,2}$/.test(e.target.value)) {
                setTmpFilterState({
                  ...tmpFilterState,
                  [value]: e.currentTarget.value,
                })
              }
            }}
            placeholder={placeholder}
            handleClear={() =>
              setTmpFilterState({
                ...tmpFilterState,
                [value]: '',
              })
            }
          />
        )
      case EnumFilterType.INPUT_STRING:
        return (
          <SzInput
            id={id}
            value={intiValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTmpFilterState({
                ...tmpFilterState,
                [value]: e.currentTarget.value,
              })
            }
            onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
              handlePasteWithTrim(e, value, tmpFilterState, setTmpFilterState)
            }
            placeholder={placeholder}
            handleClear={() =>
              setTmpFilterState({
                ...tmpFilterState,
                [value]: '',
              })
            }
          />
        )
      case EnumFilterType.INPUT_WITH_OPERATOR:
        return (
          <FormDropdownInput
            options={
              filterItems.find((item) => item.name === value)?.data ?? []
            }
            onChange={(newValue: {
              operator: string
              label: string
              value: string | number
            }) => {
              setTmpFilterState({
                ...tmpFilterState,
                [value]: newValue,
              })
            }}
          />
        )
      default:
        return ''
    }
  }

  const displayAllElementsFilter = () => {
    return (
      <div
        className={
          !isMobileMode
            ? `filter_module_desktop_filters py-4 px-3
                ${showTags && 'filter_module_bottom-border'}`
            : 'filter_module_mobile_filters'
        }
      >
        <div className="d-lg-flex flex-wrap d-block">
          {filterBuilder.static.length > 0 &&
            filterBuilder.static.map((element) => {
              return (
                <div
                  key={element.name}
                  className={
                    !isMobileMode ? `${element.className} mr-3` : 'row-filter'
                  }
                >
                  <div className="searchBar_label">{element.label}</div>
                  {renderFilterComponent(
                    filterBuilder.static,
                    tmpStaticFilterState,
                    setTmpStaticFilterState,
                    element.type,
                    element.name
                  )}
                </div>
              )
            })}
          {filterBuilder.dynamic.length > 0 && (
            <>
              <div className={!isMobileMode ? '' : 'row-filter'}>
                <div className="searchBar_label">{t('filter.addFilter')}</div>
                <div className="custom-select-desktop">
                  <CustomSelect
                    id="dynamic-filter"
                    options={filterOptions}
                    value={selectedFilter}
                    className="searchBar_select"
                    name="input-1"
                    onChange={onChangeFilter}
                    height={47}
                    isSearchable={false}
                  />
                  {renderFilterComponent(
                    filterBuilder.dynamic,
                    tmpDynamicFilterState,
                    setTmpDynamicFilterState,
                    selectedFilter.type ?? '',
                    selectedFilter.value
                  )}
                  {isDynamicFilterStateChanged && (
                    <button
                      type="button"
                      className="add-custom-filter-btn"
                      onClick={addTag}
                    >
                      <SvgIcon name="addSquare" />
                    </button>
                  )}
                </div>
              </div>
              {isMobileMode && showTags && (
                <Tags
                  fixedHeight={fixedHeight}
                  filterOptions={filterOptions}
                  filterValues={filterValues}
                  setFiltersValues={setFiltersValues}
                  resetFilter={resetFilter}
                  setIsSubmitDisabled={setIsSubmitDisabled}
                />
              )}
            </>
          )}
        </div>
        {actionsFilter()}
      </div>
    )
  }

  const actionsFilter = () => {
    return (
      <div className="filter_buttons">
        <ButtonSquareIcon
          iconName="synchronize"
          onClick={resetFilter}
          disabled={isSubmitDisabled}
        />
        <Button
          data-cy="applyFilter"
          disabled={
            isSubmitDisabled ||
            showLimitDatesError ||
            isMonthPickerPartiallyFilled
          }
          onClick={applyFilter}
          className="apply-filter"
        >
          <p>{t('filter.applyFilters')}</p>
        </Button>
      </div>
    )
  }

  const addTag = () => {
    const currentValue = tmpDynamicFilterState[selectedFilter.value]
    const trimmedValue =
      typeof currentValue === 'string' ? currentValue.trim() : currentValue

    if (typeof trimmedValue === 'string' && trimmedValue === '') {
      return
    }

    if (
      !filterValues[selectedFilter.value].includes(trimmedValue) &&
      selectedFilter.multiple
    ) {
      filterValues[selectedFilter.value].push(trimmedValue)
    } else {
      filterValues[selectedFilter.value][0] = trimmedValue
    }
    setTmpDynamicFilterState(initializFiltersString(filterBuilder.dynamic))
    setSelectedFilter(defaultFilter)
    setIsSubmitDisabled(false)
  }

  return !isMobileMode ? (
    <div
      id="filter-module-desktop"
      className={`${className} filter_module_desktop custom-dynamic-filter p-0
          ${fixedHeight && 'filter_module_desktop_fixed-height'}`}
    >
      {(filterBuilder.dynamic.length > 0 || filterBuilder.static.length > 0) &&
        displayAllElementsFilter()}
      {showTags && (
        <Tags
          fixedHeight={fixedHeight}
          filterOptions={filterOptions}
          filterValues={filterValues}
          setFiltersValues={setFiltersValues}
          resetFilter={resetFilter}
          setIsSubmitDisabled={setIsSubmitDisabled}
        />
      )}
    </div>
  ) : (
    <div className={`${className} filter_module_mobile`}>
      <Button
        className="modal_filter_button mb-3"
        onClick={() => setShowModal(true)}
      >
        <p>{t('filter.filter')}</p>
      </Button>
      <Modal
        show={showModal}
        onHide={onHideUpdate}
        centered
        backdrop="static"
        size="xl"
        className="mobile_filter_modal"
      >
        <Modal.Header closeButton className="modal-header">
          <Modal.Title>{t('filter.advancedSearch')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(filterBuilder.dynamic.length > 0 ||
            filterBuilder.static.length > 0) &&
            displayAllElementsFilter()}
        </Modal.Body>
      </Modal>
    </div>
  )
}

FilterBuilder.defaultProps = {
  className: '',
  initialFilterValues: undefined,
}

export default FilterBuilder
